import React, { useMemo } from 'react'
import queryString from 'query-string'
import {observer} from 'mobx-react-lite'
import {Spinner} from '../../../../components/spinner'
import {useSearchParams} from '../../../../hooks/use-search-params'
import {Seo} from '../../../../components/seo'
import {Box, Center, Container, Flex, Stack, Text, BaseButton} from '../../../../vanilla'
import {Breadcrumbs} from '../../../../components/breadcrumbs'
import {
  CheckIcon,
  ChevronLeftIcon,
  MapMarkerLightIcon,
  PhoneIcon,
} from '../../../../components/icons'
import useNavigation from '../../../../hooks/use-navigation'
import {StoreDetailMap} from '../store-detail-map'

import {
  storeDetailAddressBox,
  storeDetailFeatureItem,
  storeDetailInfoBoxColumn,
  storeDetailMapBox,
} from './style.css'

import {useStoreDetails} from './use-store-details'
import useCustomSitePreference from '../../../../hooks/forms/useCustomSitePreference'
import {storeHoursBlock} from './storeHoursBlock' 
import { buildStructuredGroceryStoreData } from '../../../../utils/structured-data'

export const StoreDetailPage = observer(function StoreDetailPage() {
  const enableSeasonalOpeningHours = useCustomSitePreference('enableSeasonalOpeningHours', false)
  const seasonalOpeningHoursText = useCustomSitePreference('seasonalOpeningHoursText', "")
  const navigate = useNavigation()
  const params = useSearchParams()
  const {store, loading, seasonalHours} = useStoreDetails(params.get('StoreID') || '')
  const isLoading = loading && !store

  const buildBackUrl = (searchVal?: string) =>
    `/store-finder?${queryString.stringify({prevSearch: searchVal || ''}, {skipEmptyString: true})}`

  const structuredData = useMemo(() => {
    if (!store) {
      return []
    }

    return [buildStructuredGroceryStoreData(store, enableSeasonalOpeningHours ? seasonalHours : null)]
  }, [store, seasonalHours, enableSeasonalOpeningHours])

  return (
    <>
      <Seo 
        title={params.get('storeName') || store?.name} 
        structuredData={structuredData} 
      />
      <Container
        paddingTop={['0px', '12px']}
        paddingX={['0px', '16px']}
        paddingBottom={['24px', '36px']}
      >
        <Stack width="full" spacing={['0px', '12px']}>
          <Breadcrumbs
            display={['none', 'block']}
            breadcrumbs={[
              {id: 'STORE_LOCATOR', name: 'Store Locator', url: '/store-finder'},
              ...(store ? [{id: `STORE_${store?.id}`, name: store?.name}] : []),
            ]}
          />

          <Flex
            display={['flex', 'none']}
            bg="white"
            gap="4px"
            align="center"
            paddingX="16px"
            paddingY="12px"
            borderBottom="1px"
            borderColor="gray200"
            onClick={() => navigate(buildBackUrl(params.get('searchVal') || ''))}
          >
            <ChevronLeftIcon style={{width: 14, height: 14}} color="gray800" />
            <Text variant="text1" lineHeight="none">
              Back
            </Text>
          </Flex>

          {isLoading ? (
            <Box width="full" bg="gray100" height="48px" />
          ) : (
            <Box
              width="full"
              display={['block', 'none']}
              paddingTop="16px"
              paddingX="16px"
              bg="white"
              paddingBottom="12px"
            >
              <Text as="h1" variant="heading1" color="gray800" lineHeight="short">
                {store?.name}
              </Text>
            </Box>
          )}

          <Stack width="full" spacing="20px">
            <Flex
              align={['stretch', 'flex-start']}
              direction={['column-reverse', 'row']}
              width="full"
              gap="8px"
            >
              <Stack
                spacing="8px"
                className={storeDetailInfoBoxColumn}
                align="flex-start"
                alignSelf="stretch"
              >
                <Box display={['none', 'flex']}>
                  <BaseButton
                    variant="unstyled"
                    alignItems="center"
                    gap="12px"
                    paddingX="28px"
                    paddingY="24px"
                    bg="white"
                    height="auto"
                    onClick={() => navigate(buildBackUrl(params.get('searchVal') || ''))}
                    data-test-selector="back-to-store-list-button"
                  >
                    <ChevronLeftIcon boxSize="24px" color="gray800" />
                    <Text variant="heading1" lineHeight="shorter">
                      Back to Store List
                    </Text>
                  </BaseButton>
                </Box>

                {isLoading ? (
                  <Center bg="white" width="full" flexGrow="1" style={{minHeight: 400}}>
                    <Spinner color="primary" />
                  </Center>
                ) : (
                  <Stack spacing="0px" bg="white" width="full" flexGrow="1">
                    <Box
                      padding="16px"
                      borderBottom="1px"
                      borderColor="gray200"
                      display={['none', 'block']}
                    >
                      <Text variant="heading1" lineHeight="shorter" data-test-selector="store-name">
                        {store?.name}
                      </Text>
                    </Box>
                    <Flex
                      padding={['0px', '16px']}
                      flexWrap="wrap"
                      justifyContent="space-between"
                      gap="8px"
                    >
                      <Stack className={storeDetailAddressBox}>
                        <Text variant="text4" lineHeight="shorter" display={['none', 'block']}>
                          Address:
                        </Text>
                        <Flex
                          gap="8px"
                          align="flex-start"
                          display={['block', 'flex']}
                          borderBottom={['1px', '0px']}
                          borderColor="gray200"
                          paddingX={['16px', '0px']}
                          paddingY={['12px', '0px']}
                        >
                          <MapMarkerLightIcon
                            color="gray800"
                            style={{width: 22, height: 'auto'}}
                            display={['none', 'inline-block']}
                          />
                          <Stack
                            direction={['row', 'column']}
                            spacing={['4px', '0px']}
                            flexWrap={['wrap', 'nowrap']}
                          >
                            {store?.address1 && (
                              <Text variant="text3" lineHeight="shorter" as="span" data-test-selector="store-address1">
                                {store.address1}
                              </Text>
                            )}
                            {store?.address2 && (
                              <Text variant="text3" lineHeight="shorter" as="span" data-test-selector="store-address2">
                                {store.address2}
                              </Text>
                            )}
                            {store?.city && (
                              <Text variant="text3" lineHeight="shorter" as="span" data-test-selector="store-city">
                                {store.city}
                              </Text>
                            )}
                            {store?.postalCode && (
                              <Text variant="text3" lineHeight="shorter" as="span" data-test-selector="store-postal-code">
                                {store.postalCode}
                              </Text>
                            )}
                          </Stack>
                        </Flex>
                        {store?.phone && (
                          <Flex gap="8px" align="flex-start" display={['none', 'flex']}>
                            <PhoneIcon boxSize="20px" color="gray800" />
                            <Text variant="text3" lineHeight="shorter" as="span" data-test-selector="store-phone">
                              {store.phone}
                            </Text>
                          </Flex>
                        )}
                      </Stack>
                      {store?.phone && (
                        <Flex
                          borderTop="1px"
                          borderBottom="1px"
                          borderColor="gray200"
                          align="center"
                          gap="8px"
                          display={['flex', 'none']}
                          paddingX={['16px', '0px']}
                          paddingY={['16px', '0px']}
                          order="2"
                          width="full"
                        >
                          <PhoneIcon boxSize="20px" color="gray800" />
                          <Text variant="text3" lineHeight="shorter" as="span" data-test-selector="store-phone">
                            {store.phone}
                          </Text>
                        </Flex>
                      )}

                      {store && storeHoursBlock(store, store?.c_storeHoursJson, "Opening Times:", "store-opening-hours")}
                      {store && enableSeasonalOpeningHours && seasonalHours && storeHoursBlock(store, seasonalHours, `${seasonalOpeningHoursText}:`)}
                      {store?.c_storeFeatures?.length && (
                        <Stack
                          spacing="8px"
                          order={['2', '0']}
                          paddingX={['16px', '0px']}
                          paddingY={['20px', '0px']}
                          flexBasis="full"
                          data-test-selector="store-features"
                        >
                          <Text variant="text4" as="span" lineHeight="shorter">
                            Store Details:
                          </Text>
                          <Stack as="ul" spacing="8px" direction="row" flexWrap="wrap">
                            {store.c_storeFeatures.map((feature) => (
                              <Flex
                                as="li"
                                align="center"
                                gap="4px"
                                className={storeDetailFeatureItem}
                                key={`${store.id}-${feature.value}`}
                              >
                                <CheckIcon boxSize="20px" color="accent1" />
                                <Text variant="text4" as="span" lineHeight="shorter">
                                  {feature.displayValue}
                                </Text>
                              </Flex>
                            ))}
                          </Stack>
                        </Stack>
                      )}
                    </Flex>
                  </Stack>
                )}
              </Stack>
              <Box
                width="full"
                className={storeDetailMapBox}
                bg="gray100"
                border="1px"
                borderColor="gray200"
              >
                {store && (
                  <StoreDetailMap
                    store={store}
                    mapOptions={{
                      center: {
                        lng: store?.longitude ?? 0,
                        lat: store?.latitude ?? 0,
                      },
                      zoom: 16,
                    }}
                  />
                )}
              </Box>
            </Flex>
          </Stack>
        </Stack>
      </Container>
    </>
  )
})
